if (!customElements.get("ecomhard-look")) {
  class EcomhardLook extends HTMLElement {
    constructor() {
      super();
      this.cards = [];
      this.galleries = [];
      this.mainProduct = null;
      this.isSubmitting = false;
      this.onChange = this.onChange.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onMainProductInput = this.onMainProductInput.bind(this);
      this.lightboxImages = [];
      this.lightboxIndex = 0;
      this.lightboxTrigger = null;
    }

    connectedCallback() {
      if (this.dataset.initialized === "true") return;
      this.dataset.initialized = "true";

      this.cacheElements();
      this.createMainProduct();
      this.createCards();
      this.createGalleries();
      this.bindEvents();

      this.cards.forEach((card) => {
        this.syncAvailability(card);
        this.updateCardDisplay(card);
        this.updateCardState(card);
      });

      this.syncMainProductFromPage();
      this.updateTotal();
      this.updateSubmitState();
    }

    disconnectedCallback() {
      this.removeEventListener("change", this.onChange);
      this.removeEventListener("click", this.onClick);
      document.removeEventListener("keydown", this.onKeydown);
      document.removeEventListener("change", this.onMainProductInput, true);
      document.removeEventListener("input", this.onMainProductInput, true);
    }

    cacheElements() {
      this.totalElement = this.querySelector("[data-look-total]");
      this.discountedTotalElement = this.querySelector("[data-look-discounted-total]");
      this.discountLabelElement = this.querySelector("[data-look-discount-label]");
      this.addButton = this.querySelector("[data-look-add]");
      this.addLabel = this.querySelector("[data-look-add-label]");
      this.defaultAddLabel = this.addLabel?.textContent.trim() || "Adicionar ao carrinho";
      this.errorElement = this.querySelector("[data-look-error]");
      this.lightbox = this.querySelector("[data-look-lightbox]");
      this.lightboxDialog = this.querySelector("[data-look-lightbox-dialog]");
      this.lightboxImage = this.querySelector("[data-look-lightbox-image]");
      this.lightboxTitle = this.querySelector("[data-look-lightbox-title]");
      this.lightboxCount = this.querySelector("[data-look-lightbox-count]");
      this.lightboxPrevious = this.querySelector("[data-look-lightbox-previous]");
      this.lightboxNext = this.querySelector("[data-look-lightbox-next]");
      this.mainPriceElement = this.querySelector("[data-look-main-price]");
      this.mainComparePriceElement = this.querySelector(
        "[data-look-main-compare-price]"
      );
      this.cartDrawer = document.querySelector("cart-drawer");
      this.mainCart = document.querySelector("cart-items");
    }

    createMainProduct() {
      const productJson = this.querySelector("[data-look-main-product-json]");
      let product = {};

      try {
        product = JSON.parse(productJson?.textContent || "{}");
      } catch (error) {
        console.error("[ECOMHARD Look] Não foi possível ler o produto principal.", error);
      }

      const variants = Array.isArray(product.variants) ? product.variants : [];
      const initialVariantId = Number(this.dataset.mainVariantId || 0);
      const initialVariant =
        variants.find((variant) => Number(variant.id) === initialVariantId) ||
        variants.find((variant) => variant.available) ||
        variants[0] ||
        null;

      this.mainProduct = {
        product,
        variants,
        currentVariant: initialVariant,
        gallery: this.querySelector("[data-look-main-product] [data-look-gallery]"),
      };
    }

    createCards() {
      const elements = Array.from(this.querySelectorAll("[data-look-product]"));

      this.cards = elements.map((element) => {
        const productJson = element.querySelector("[data-look-product-json]");
        let product = {};

        try {
          product = JSON.parse(productJson?.textContent || "{}");
        } catch (error) {
          console.error("[ECOMHARD Look] Não foi possível ler um produto do look.", error);
        }

        const variants = Array.isArray(product.variants) ? product.variants : [];
        const hasOnlyDefaultVariant =
          element.dataset.hasOnlyDefaultVariant === "true" ||
          product.has_only_default_variant === true;

        return {
          element,
          product,
          variants,
          maxOptions: hasOnlyDefaultVariant ? 0 : Number(product.options?.length || 0),
          defaultVariant:
            hasOnlyDefaultVariant
              ? variants.find((variant) => variant.available) || variants[0] || null
              : null,
          selections: new Map(),
          toggle: element.querySelector("[data-look-toggle]"),
          inputs: Array.from(element.querySelectorAll("[data-look-option]")),
          priceElement: element.querySelector("[data-look-price]"),
          comparePriceElement: element.querySelector("[data-look-compare-price]"),
          gallery: element.querySelector("[data-look-gallery]"),
        };
      });
    }

    createGalleries() {
      const galleries = Array.from(this.querySelectorAll("[data-look-gallery]"));

      this.galleries = galleries.map((element) => ({
        element,
        slides: Array.from(element.querySelectorAll("[data-look-gallery-slide]")),
        currentIndex: 0,
        counter: element.querySelector("[data-look-gallery-count]"),
      }));
    }

    bindEvents() {
      this.addEventListener("change", this.onChange);
      this.addEventListener("click", this.onClick);
      document.addEventListener("keydown", this.onKeydown);
      document.addEventListener("change", this.onMainProductInput, true);
      document.addEventListener("input", this.onMainProductInput, true);
    }

    normalize(value) {
      return String(value ?? "").trim().toLocaleLowerCase("pt-BR");
    }

    getVariantOptionValue(variant, position) {
      return variant?.[`option${position}`] ?? "";
    }

    findCardFromElement(element) {
      const cardElement = element.closest("[data-look-product]");
      return this.cards.find((card) => card.element === cardElement) || null;
    }

    getSelectedVariant(card) {
      if (card.defaultVariant) return card.defaultVariant;
      if (!card.maxOptions || card.selections.size !== card.maxOptions) return null;

      return (
        card.variants.find((variant) => {
          if (!variant.available) return false;

          for (let position = 1; position <= card.maxOptions; position += 1) {
            const selectedValue = card.selections.get(String(position));
            if (
              this.normalize(this.getVariantOptionValue(variant, position)) !==
              this.normalize(selectedValue)
            ) {
              return false;
            }
          }

          return true;
        }) || null
      );
    }

    getDisplayVariant(card) {
      return (
        this.getSelectedVariant(card) ||
        card.variants.find((variant) => variant.available) ||
        card.variants[0] ||
        null
      );
    }

    canSelectOption(card, position, value) {
      const proposedSelection = new Map(card.selections);
      proposedSelection.set(String(position), value);

      return card.variants.some((variant) => {
        if (!variant.available) return false;

        for (const [selectedPosition, selectedValue] of proposedSelection.entries()) {
          if (
            this.normalize(this.getVariantOptionValue(variant, selectedPosition)) !==
            this.normalize(selectedValue)
          ) {
            return false;
          }
        }

        return true;
      });
    }

    syncAvailability(card) {
      card.inputs.forEach((input) => {
        const position = String(input.dataset.optionPosition || "");
        const value =
          input.tagName === "SELECT"
            ? null
            : input.dataset.optionValue || input.value;

        if (input.tagName === "SELECT") {
          Array.from(input.options).forEach((option) => {
            if (!option.value) {
              option.disabled = false;
              return;
            }

            const available = this.canSelectOption(card, position, option.value);
            const selected = this.normalize(input.value) === this.normalize(option.value);
            option.disabled = !available && !selected;
          });
          return;
        }

        const available = this.canSelectOption(card, position, value);
        const selected =
          this.normalize(card.selections.get(position)) === this.normalize(value);
        input.disabled = !available && !selected;

        const wrapper = input.closest("[data-look-option-wrap]");
        wrapper?.classList.toggle("is-unavailable", !available);
        wrapper?.setAttribute("aria-disabled", String(!available));
      });
    }

    updateCardDisplay(card) {
      const variant = this.getDisplayVariant(card);
      if (!variant) return;

      if (card.priceElement) {
        card.priceElement.textContent = this.formatMoney(Number(variant.price || 0));
      }

      if (card.comparePriceElement) {
        const compareAtPrice = Number(variant.compare_at_price || 0);
        const price = Number(variant.price || 0);
        const hasCompareAtPrice = compareAtPrice > price;

        card.comparePriceElement.hidden = !hasCompareAtPrice;
        if (hasCompareAtPrice) {
          card.comparePriceElement.textContent = this.formatMoney(compareAtPrice);
        }
      }

      const featuredImageId = Number(variant.featured_image?.id || variant.featured_media?.id || 0);
      if (featuredImageId) {
        this.goToGalleryImage(card.gallery, featuredImageId);
      }
    }

    updateMainProductDisplay() {
      const variant = this.mainProduct?.currentVariant;
      if (!variant) return;

      if (this.mainPriceElement) {
        this.mainPriceElement.textContent = this.formatMoney(Number(variant.price || 0));
      }

      if (this.mainComparePriceElement) {
        const compareAtPrice = Number(variant.compare_at_price || 0);
        const price = Number(variant.price || 0);
        const hasCompareAtPrice = compareAtPrice > price;

        this.mainComparePriceElement.hidden = !hasCompareAtPrice;
        if (hasCompareAtPrice) {
          this.mainComparePriceElement.textContent = this.formatMoney(compareAtPrice);
        }
      }

      const featuredImageId = Number(variant.featured_image?.id || variant.featured_media?.id || 0);
      if (featuredImageId) {
        this.goToGalleryImage(this.mainProduct.gallery, featuredImageId);
      }
    }

    updateCardState(card) {
      const selected = Boolean(card.toggle?.checked);
      card.element.classList.toggle("is-selected", selected);
      card.element.setAttribute("data-selected", String(selected));
    }

    getSelectedCards() {
      return this.cards.filter((card) => card.toggle?.checked);
    }

    getMainVariantFromPage() {
      if (!this.mainProduct) return null;

      const formInput = document.querySelector(
        'form[data-type="add-to-cart-form"] input[name="id"]:not([disabled])'
      );
      const variantId = Number(formInput?.value || 0);

      return (
        this.mainProduct.variants.find((variant) => Number(variant.id) === variantId) ||
        this.mainProduct.currentVariant ||
        this.mainProduct.variants.find((variant) => variant.available) ||
        this.mainProduct.variants[0] ||
        null
      );
    }

    syncMainProductFromPage() {
      if (!this.mainProduct) return;

      const variant = this.getMainVariantFromPage();
      if (!variant || this.mainProduct.currentVariant?.id === variant.id) return;

      this.mainProduct.currentVariant = variant;
      this.updateMainProductDisplay();
      this.updateTotal();
      this.updateSubmitState();
    }

    onMainProductInput(event) {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;

      if (
        target.closest("#MainProduct-") ||
        target.matches('form[data-type="add-to-cart-form"] input[name="id"]') ||
        target.matches('form[data-type="add-to-cart-form"] select') ||
        target.matches('form[data-type="add-to-cart-form"] input[type="radio"]')
      ) {
        window.setTimeout(() => this.syncMainProductFromPage(), 0);
      }
    }

    onChange(event) {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;

      const toggle = target.closest("[data-look-toggle]");
      if (toggle) {
        const card = this.findCardFromElement(toggle);
        if (!card) return;

        this.updateCardState(card);
        this.updateTotal();
        this.updateSubmitState();
        this.clearError();
        return;
      }

      const optionInput = target.closest("[data-look-option]");
      if (!optionInput || optionInput.disabled) return;

      const card = this.findCardFromElement(optionInput);
      if (!card) return;

      const position = String(optionInput.dataset.optionPosition || "");
      const value = optionInput.value;

      if (!value) {
        card.selections.delete(position);
      } else {
        card.selections.set(position, value);
        if (card.toggle) card.toggle.checked = true;
      }

      this.syncAvailability(card);
      this.updateCardDisplay(card);
      this.updateCardState(card);
      this.updateTotal();
      this.updateSubmitState();
      this.clearError();
    }

    onClick(event) {
      const lightboxClose = event.target.closest("[data-look-lightbox-close]");
      if (lightboxClose) {
        event.preventDefault();
        this.closeLightbox();
        return;
      }

      const lightboxPrevious = event.target.closest("[data-look-lightbox-previous]");
      if (lightboxPrevious) {
        event.preventDefault();
        this.moveLightbox(-1);
        return;
      }

      const lightboxNext = event.target.closest("[data-look-lightbox-next]");
      if (lightboxNext) {
        event.preventDefault();
        this.moveLightbox(1);
        return;
      }

      const previous = event.target.closest("[data-look-gallery-previous]");
      if (previous) {
        event.preventDefault();
        const gallery = previous.closest("[data-look-gallery]");
        this.moveGallery(gallery, -1);
        return;
      }

      const next = event.target.closest("[data-look-gallery-next]");
      if (next) {
        event.preventDefault();
        const gallery = next.closest("[data-look-gallery]");
        this.moveGallery(gallery, 1);
        return;
      }

      const lightboxTrigger = event.target.closest("[data-look-lightbox-trigger]");
      if (lightboxTrigger) {
        event.preventDefault();
        const card = this.findCardFromElement(lightboxTrigger);
        if (card) this.openLightbox(card, lightboxTrigger);
        return;
      }

      const addButton = event.target.closest("[data-look-add]");
      if (addButton) {
        event.preventDefault();
        this.addSelectedProducts();
      }
    }

    onKeydown(event) {
      const lightboxIsOpen = Boolean(this.lightbox && !this.lightbox.hidden);

      if (lightboxIsOpen) {
        if (event.key === "Escape") {
          event.preventDefault();
          this.closeLightbox();
          return;
        }

        if (event.key === "ArrowLeft") {
          event.preventDefault();
          this.moveLightbox(-1);
          return;
        }

        if (event.key === "ArrowRight") {
          event.preventDefault();
          this.moveLightbox(1);
        }
        return;
      }

      const target = event.target;
      if (!(target instanceof Element)) return;

      const lightboxTrigger = target.closest("[data-look-lightbox-trigger]");
      if (!lightboxTrigger || (event.key !== "Enter" && event.key !== " ")) return;

      event.preventDefault();
      const card = this.findCardFromElement(lightboxTrigger);
      if (card) this.openLightbox(card, lightboxTrigger);
    }

    getLightboxImages(card) {
      if (!card?.element) return [];

      return Array.from(
        card.element.querySelectorAll(".eh-look__gallery--product img[data-look-lightbox-src]")
      )
        .map((image) => ({
          src: image.dataset.lookLightboxSrc || image.currentSrc || image.src || "",
          alt: image.alt || card.product?.title || "",
        }))
        .filter((image) => Boolean(image.src));
    }

    openLightbox(card, trigger) {
      const images = this.getLightboxImages(card);
      if (!this.lightbox || !images.length) return;

      const miniGallery = this.getGallery(card.gallery);
      const initialIndex = Math.max(
        0,
        Math.min(miniGallery?.currentIndex || 0, images.length - 1)
      );

      this.lightboxImages = images;
      this.lightboxTrigger = trigger || null;
      this.lightbox.hidden = false;
      this.lightbox.setAttribute("aria-hidden", "false");
      document.documentElement.classList.add("eh-look-lightbox-open");
      document.body.classList.add("eh-look-lightbox-open");

      if (this.lightboxTitle) {
        this.lightboxTitle.textContent = card.product?.title || "";
      }

      this.showLightboxSlide(initialIndex);
      window.requestAnimationFrame(() => this.lightboxDialog?.focus());
    }

    closeLightbox() {
      if (!this.lightbox || this.lightbox.hidden) return;

      this.lightbox.hidden = true;
      this.lightbox.setAttribute("aria-hidden", "true");
      document.documentElement.classList.remove("eh-look-lightbox-open");
      document.body.classList.remove("eh-look-lightbox-open");

      if (this.lightboxImage) {
        this.lightboxImage.removeAttribute("src");
        this.lightboxImage.alt = "";
      }

      this.lightboxImages = [];
      this.lightboxIndex = 0;

      const trigger = this.lightboxTrigger;
      this.lightboxTrigger = null;
      trigger?.focus?.();
    }

    moveLightbox(offset) {
      if (!this.lightboxImages.length) return;
      this.showLightboxSlide(this.lightboxIndex + offset);
    }

    showLightboxSlide(index) {
      const images = this.lightboxImages;
      if (!images.length || !this.lightboxImage) return;

      const safeIndex = ((index % images.length) + images.length) % images.length;
      const image = images[safeIndex];
      this.lightboxIndex = safeIndex;

      this.lightboxImage.src = image.src;
      this.lightboxImage.alt = image.alt;

      if (this.lightboxCount) {
        this.lightboxCount.textContent = `${safeIndex + 1} / ${images.length}`;
      }

      const hasMultipleImages = images.length > 1;
      if (this.lightboxPrevious) this.lightboxPrevious.disabled = !hasMultipleImages;
      if (this.lightboxNext) this.lightboxNext.disabled = !hasMultipleImages;
    }

    getGallery(galleryElement) {
      return this.galleries.find((gallery) => gallery.element === galleryElement) || null;
    }

    moveGallery(galleryElement, offset) {
      const gallery = this.getGallery(galleryElement);
      if (!gallery || gallery.slides.length < 2) return;

      const nextIndex =
        (gallery.currentIndex + offset + gallery.slides.length) % gallery.slides.length;
      this.showGallerySlide(gallery, nextIndex);
    }

    goToGalleryImage(galleryElement, imageId) {
      const gallery = this.getGallery(galleryElement);
      if (!gallery || !imageId) return;

      const index = gallery.slides.findIndex(
        (slide) => Number(slide.dataset.lookImageId || 0) === Number(imageId)
      );

      if (index >= 0) this.showGallerySlide(gallery, index);
    }

    showGallerySlide(gallery, index) {
      if (!gallery || index < 0 || index >= gallery.slides.length) return;

      gallery.currentIndex = index;
      gallery.slides.forEach((slide, slideIndex) => {
        const isCurrent = slideIndex === index;
        slide.hidden = !isCurrent;
        slide.setAttribute("aria-hidden", String(!isCurrent));
      });

      if (gallery.counter) {
        gallery.counter.textContent = `${index + 1} / ${gallery.slides.length}`;
      }
    }

    parseDiscountValue(rawValue) {
      let value = String(rawValue ?? "").trim();
      if (!value) return 0;

      value = value.replace(/[^\d,.-]/g, "");

      const commaIndex = value.lastIndexOf(",");
      const dotIndex = value.lastIndexOf(".");

      if (commaIndex > -1 && dotIndex > -1) {
        if (commaIndex > dotIndex) {
          value = value.replace(/\./g, "").replace(",", ".");
        } else {
          value = value.replace(/,/g, "");
        }
      } else if (commaIndex > -1) {
        value = value.replace(",", ".");
      }

      const parsedValue = Number.parseFloat(value);
      return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : 0;
    }

    getDiscountConfig() {
      const type = this.normalize(this.dataset.lookDiscountType);
      const value = this.parseDiscountValue(this.dataset.lookDiscountValue);

      if (!value) return null;

      if (type.includes("porcent")) {
        return {
          type: "percentage",
          value: Math.min(value, 100),
        };
      }

      if (type.includes("fix")) {
        return {
          type: "fixed",
          value,
        };
      }

      return null;
    }

    getTotals() {
      const mainVariant = this.mainProduct?.currentVariant;
      const selectedProductsTotal = this.getSelectedCards().reduce((total, card) => {
        const variant = this.getSelectedVariant(card);
        return total + Number(variant?.price || 0);
      }, 0);
      const total = Number(mainVariant?.price || 0) + selectedProductsTotal;
      const discount = this.getDiscountConfig();

      if (!discount || total <= 0) {
        return {
          total,
          discountAmount: 0,
          discountedTotal: total,
          discount,
        };
      }

      const rawDiscountAmount =
        discount.type === "percentage"
          ? Math.round((total * discount.value) / 100)
          : Math.round(discount.value * 100);
      const discountAmount = Math.min(Math.max(rawDiscountAmount, 0), total);

      return {
        total,
        discountAmount,
        discountedTotal: Math.max(total - discountAmount, 0),
        discount,
      };
    }

    formatPercentage(value) {
      try {
        return new Intl.NumberFormat("pt-BR", {
          maximumFractionDigits: 2,
        }).format(Number(value || 0));
      } catch (error) {
        return String(value || 0).replace(".", ",");
      }
    }

    updateTotal() {
      if (!this.totalElement) return;

      const { total, discountAmount, discountedTotal, discount } = this.getTotals();
      this.totalElement.textContent = this.formatMoney(total);

      if (this.discountedTotalElement) {
        this.discountedTotalElement.textContent = this.formatMoney(discountedTotal);
      }

      if (this.discountLabelElement) {
        if (discount && discountAmount > 0) {
          this.discountLabelElement.hidden = false;
          this.discountLabelElement.textContent =
            discount.type === "percentage"
              ? `-${this.formatPercentage(discount.value)}%`
              : `-${this.formatMoney(discountAmount)}`;
        } else {
          this.discountLabelElement.hidden = true;
          this.discountLabelElement.textContent = "";
        }
      }
    }

    updateSubmitState() {
      if (!this.addButton) return;

      const mainVariant = this.mainProduct?.currentVariant;
      const selectedCards = this.getSelectedCards();
      const hasCardsWithoutSelectedVariant = selectedCards.some(
        (card) => !this.getSelectedVariant(card)
      );
      const allSelectedCardsValid = !hasCardsWithoutSelectedVariant;
      const canSubmit =
        Boolean(mainVariant?.available) &&
        selectedCards.length > 0 &&
        allSelectedCardsValid &&
        !this.isSubmitting;

      if (this.addLabel) {
        this.addLabel.textContent =
          selectedCards.length > 0 && hasCardsWithoutSelectedVariant
            ? "Selecione as variantes"
            : this.defaultAddLabel;
      }

      this.addButton.disabled = !canSubmit;
      this.addButton.setAttribute("aria-disabled", String(!canSubmit));
    }

    formatMoney(cents) {
      if (typeof window.formatMoney === "function") {
        return window.formatMoney(
          cents,
          window.FoxThemeSettings?.money_format || this.dataset.moneyFormat
        );
      }

      const amount = Number(cents || 0) / 100;
      const currency = window.Shopify?.currency?.active || "BRL";

      try {
        return new Intl.NumberFormat("pt-BR", {
          style: "currency",
          currency,
        }).format(amount);
      } catch (error) {
        return `R$ ${amount.toFixed(2).replace(".", ",")}`;
      }
    }

    getSectionsToRender() {
      let sections = [];

      document.documentElement.dispatchEvent(
        new CustomEvent("cart:grouped-sections", {
          bubbles: true,
          detail: { sections },
        })
      );

      if (this.cartDrawer?.getSectionsToRender) {
        sections = [
          ...sections,
          ...this.cartDrawer.getSectionsToRender().map((section) => section.id),
        ];
      }

      if (this.mainCart?.getSectionsToRender) {
        sections = [
          ...sections,
          ...this.mainCart.getSectionsToRender().map((section) => section.section),
        ];
      }

      return [...new Set(sections.filter(Boolean))];
    }

    async addSelectedProducts() {
      if (this.isSubmitting) return;

      const mainVariant = this.mainProduct?.currentVariant;
      const selectedCards = this.getSelectedCards();
      const selectedVariants = selectedCards.map((card) => this.getSelectedVariant(card));

      if (!mainVariant?.available || !selectedCards.length || selectedVariants.some((variant) => !variant)) {
        this.updateSubmitState();
        return;
      }

      this.setLoading(true);
      this.clearError();

      const sections = this.getSectionsToRender();
      const payload = {
        items: [
          { id: Number(mainVariant.id), quantity: 1 },
          ...selectedVariants.map((variant) => ({ id: Number(variant.id), quantity: 1 })),
        ],
        sections: sections.join(","),
        sections_url: window.location.pathname,
      };

      try {
        const response = await fetch(
          this.dataset.cartAddUrl || window.FoxThemeSettings?.routes?.cart_add_url || "/cart/add.js",
          {
            method: "POST",
            headers: {
              Accept: "application/javascript",
              "Content-Type": "application/json",
              "X-Requested-With": "XMLHttpRequest",
            },
            body: JSON.stringify(payload),
          }
        );

        const state = await response.json();

        if (!response.ok || state.status) {
          throw new Error(state.description || state.message || "Não foi possível adicionar os produtos ao carrinho.");
        }

        if (window.FoxThemeEvents && typeof PUB_SUB_EVENTS !== "undefined") {
          window.FoxThemeEvents.emit(PUB_SUB_EVENTS.cartUpdate, state);
        }

        document.dispatchEvent(
          new CustomEvent("product-ajax:added", {
            detail: { product: state, source: "ecomhard-look" },
          })
        );

        if (!this.cartDrawer && !this.mainCart && window.FoxThemeCartHelpers?.getState) {
          const cart = await window.FoxThemeCartHelpers.getState();
          window.FoxThemeCartHelpers.updateCartCount?.(cart.item_count);
        }
      } catch (error) {
        console.error("[ECOMHARD Look]", error);
        this.showError(error.message || "Não foi possível adicionar os produtos ao carrinho.");
      } finally {
        this.setLoading(false);
      }
    }

    setLoading(isLoading) {
      this.isSubmitting = isLoading;
      this.closest(".eh-look")?.classList.toggle("is-loading", isLoading);
      this.updateSubmitState();
    }

    showError(message) {
      if (!this.errorElement) return;
      this.errorElement.hidden = false;
      this.errorElement.textContent = message;
    }

    clearError() {
      if (!this.errorElement) return;
      this.errorElement.hidden = true;
      this.errorElement.textContent = "";
    }
  }

  customElements.define("ecomhard-look", EcomhardLook);
}
